/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

/**
 * Roles within a customer (clinic) tenant. Drives the customer login flow,
 * the role-aware router, and the `@Permissions(...)` guard on every controller.
 *
 * Adding a new customer role here requires:
 *   - a corresponding seed row in `core.roles`,
 *   - permission rows in `core.role_permissions`,
 *   - a portal shell or sub-page added to the customer router.
 */
export const CUSTOMER_ROLES = [
  'SUPER_ADMIN',
  'CLINIC_ADMIN',
  'THERAPIST',
  'CONSULTANT',
  'PARENT',
  'FINANCE',
] as const;

export type CustomerRole = (typeof CUSTOMER_ROLES)[number];

/**
 * Sub-roles within the Super Admin (Product Admin) portal. Never exposed to
 * customers. Drives the `@StaffPermissions(...)` guard on `/sa/*` controllers.
 */
export const STAFF_ROLES = [
  'PLATFORM_OWNER',
  'CUSTOMER_SUCCESS',
  'SUPPORT',
  'READONLY_AUDITOR',
] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

/** Convenience union — any role recognised by the platform. */
export type AnyRole = CustomerRole | StaffRole;

/**
 * Customer-portal route prefixes — kept in sync with the React router and the
 * Figma navigation.
 */
export const ROLE_HOME_PATH: Readonly<Record<CustomerRole, string>> = Object.freeze({
  // Super Admin is a clinic-level role that owns Settings + Roles; it runs in the
  // admin portal. Consultant is a therapist employment type → therapist portal.
  SUPER_ADMIN: '/admin',
  CLINIC_ADMIN: '/admin',
  THERAPIST: '/therapist',
  CONSULTANT: '/therapist',
  PARENT: '/parent',
  FINANCE: '/finance',
});

export const STAFF_HOME_PATH = '/sa';
